// Notifikace přihlášeného — zvonek v hlavičce (F4 4.3 „notifikace řidiči“; kontrakt tag Notifikace, C3):
//   GET  /ja/notifikace?neprectene=&strana=&velikost=   seznam (nadpis, podtitul, závažnost, odkaz, stav, kdy)
//                                                       + `neprectene` = počet ve stavu `nova` pro zvonek
//   POST /ja/notifikace/precteno { ids? | vse? }        označit přečtené → { oznaceno, neprectene }
// Počet se načte po přihlášení a obnovuje každou minutu (react-query `refetchInterval`); jen vlastní notifikace,
// audit se nevede. Kam vede klik: `odkaz` ze serveru, jinak podle `entita` + `entita_id` (zakázka → karta).
import type { Schema } from '@ecoser/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { formatDatumCas } from '../formaty';
import { klicPolozky, odkazNaPolozku, type PolozkaFronty } from './k-vyrizeni';
import { api } from './klient';

export type Notifikace = Schema<'Notifikace'> & { id: string };
export type NotifikaceSeznam = Omit<Schema<'NotifikaceSeznam'>, 'polozky'> & {
  polozky: Notifikace[];
};
export type ZavaznostNotifikace = Notifikace['zavaznost'];
export type PrectenoOdpoved = { oznaceno: number; neprectene: number };

export const KLIC_NOTIFIKACE = ['ja', 'notifikace'] as const;
/** Obnova počtu ve zvonku (zadání 4.3: každou minutu). */
export const INTERVAL_OBNOVY_MS = 60_000;
/** Kolik posledních notifikací panel ukáže. */
export const VELIKOST_PANELU = 20;

export function nacistNotifikace(signal?: AbortSignal): Promise<NotifikaceSeznam> {
  return api.get<NotifikaceSeznam>(`/ja/notifikace?strana=1&velikost=${VELIKOST_PANELU}`, {
    signal,
  });
}

/** Seznam pro zvonek; obnovuje se každou minutu i na pozadí (řidič čeká na „odesláno řidiči“). */
export function useNotifikace(povoleno = true) {
  return useQuery({
    queryKey: KLIC_NOTIFIKACE,
    queryFn: ({ signal }) => nacistNotifikace(signal),
    enabled: povoleno,
    refetchInterval: INTERVAL_OBNOVY_MS,
    refetchIntervalInBackground: true,
    // Endpoint může na starším serveru chybět (404) — bez opakování, zvonek zůstane bez počtu.
    retry: false,
  });
}

export const oznacitPrectene = (telo: { ids?: string[]; vse?: boolean }) =>
  api.post<PrectenoOdpoved>('/ja/notifikace/precteno', telo);

export function obnovitNotifikace(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: KLIC_NOTIFIKACE });
}

/** Cíle prokliku podle `entita` (kontrakt: zakazka, zakaznik, poptavka, iso_udalost, hlaseni …). */
const CESTY_ENTIT: Readonly<Record<string, string>> = {
  zakazka: '/zakazky',
  zakaznik: '/zakaznici',
  zamestnanec: '/zamestnanci',
  vozidlo: '/vozidla',
  kalendar_poznamka: '/kalendar',
  // Krok 12.1b: zvonek „faktura přidělena ke schválení / rozhodnuta“ a „dávka čeká na podpis“.
  prijata_faktura: '/ucetnictvi/prijate-faktury',
};

/**
 * Notifikace, které míří na položku fronty K vyřízení (05/18: „klik na položku zvonku skočí do téže fronty na příslušný
 * řádek“): typ a entita notifikace → druh položky fronty. Klíč řádku je `druh:id` záznamu, který notifikace nese.
 */
const POLOZKA_FRONTY_Z_NOTIFIKACE: ReadonlyArray<{
  typ: string;
  entita: string;
  druh: PolozkaFronty['druh'];
}> = [
  { typ: 'zakazka_ke_kontrole', entita: 'zakazka', druh: 'zakazka_ke_kontrole' },
  { typ: 'hlaseni_nove', entita: 'hlaseni', druh: 'hlaseni' },
  { typ: 'posta_navrh_novy', entita: 'email_navrh', druh: 'navrh_z_posty' },
  { typ: 'iso_udalosti', entita: 'iso_udalost', druh: 'iso_udalost' },
  { typ: 'iso_hlaseni_mobil', entita: 'iso_udalost', druh: 'iso_udalost' },
];

/**
 * Odkaz uložený v notifikaci, který účet otevřít nemůže nebo který vede na neexistující cestu (nálezy RID-F3 a DIS-N01,
 * W24). Upozornění o jízdě (přidělena, změna, vrácena, stornována) od PIL-03 sestavuje server podle sekcí příjemce;
 * tohle opravuje starší, uložené dřív, a odkazy ostatních zdrojů (signály, jiné moduly):
 *   * `/pozadavky-svozu…` je cesta API — stránka je `/svoz-pozadavky`;
 *   * `/zakazky/{id}` bez sekce Zakázky (řidič, skladník) vede na „Nemáte přístup“ — jejich jízda je v Můj den.
 */
export function opravitOdkaz(odkaz: string, sekce?: readonly string[]): string {
  if (/^\/pozadavky-svozu(?:[/?#]|$)/.test(odkaz)) return '/svoz-pozadavky?stav=ceka';
  const zakazka = /^\/zakazky\/([^/?#]+)/.exec(odkaz);
  if (zakazka && sekce && !sekce.includes('zakazky') && sekce.includes('muj_den')) {
    return `/muj-den/${zakazka[1]}`;
  }
  return odkaz;
}

/**
 * Kam notifikace vede: u věcí z fronty K vyřízení (účet má sekci a server notifikaci cíl dal) na řádek ve frontě,
 * jinak `odkaz` ze serveru, jinak karta entity; bez cíle undefined (jen se označí přečtená). Zvonek zůstává
 * upozorněním — stará obrazovka věci se otevírá z fronty tlačítkem Otevřít.
 */
export function cilNotifikace(
  n: Pick<Notifikace, 'odkaz' | 'entita' | 'entita_id'> & { typ?: string },
  moznosti: { fronta?: boolean; sekce?: readonly string[] } = {},
): string | undefined {
  if (moznosti.fronta && n.odkaz && n.entita_id) {
    const radek = POLOZKA_FRONTY_Z_NOTIFIKACE.find((p) => p.typ === n.typ && p.entita === n.entita);
    if (radek) return odkazNaPolozku(klicPolozky(radek.druh, n.entita_id));
  }
  if (n.odkaz) return opravitOdkaz(n.odkaz, moznosti.sekce);
  // Dávka se otevírá parametrem na obrazovce Banka (Dávky a protokol), ne vlastní cestou.
  if (n.entita === 'banka_davka' && n.entita_id) {
    return `/ucetnictvi/banka?davka=${encodeURIComponent(n.entita_id)}`;
  }
  const zaklad = n.entita ? CESTY_ENTIT[n.entita] : undefined;
  if (!zaklad || !n.entita_id) return undefined;
  return `${zaklad}/${encodeURIComponent(n.entita_id)}`;
}

/** Popisek závažnosti pro čtečku (tečka je jen barva). */
export const ZAVAZNOSTI: Readonly<Record<ZavaznostNotifikace, string>> = {
  info: 'Informace',
  upozorneni: 'Upozornění',
  kriticke: 'Kritické',
};

/** Čas notifikace: do hodiny „před 5 min“ (pod minutu „právě teď“), jinak `9. 9. 2026 08:05` (docs/04). */
export function popisKdy(kdy: string, ted: Date = new Date()): string {
  const t = new Date(kdy).getTime();
  if (Number.isNaN(t)) return '';
  const minut = Math.floor((ted.getTime() - t) / 60_000);
  if (minut < 1) return 'právě teď';
  if (minut < 60) return `před ${minut} min`;
  return formatDatumCas(kdy);
}
